<template>
  <v-main>
    <v-container fluid>
      <v-toolbar class="elevation-0">
        <v-toolbar-title>Analytics Huggy</v-toolbar-title>
      </v-toolbar>

      <FilterBar
        :filters="filters"
        :channels="availableChannels"
        :auto-refresh="autoRefresh"
        @update:filters="updateFilters"
        @update:autoRefresh="setAutoRefresh"
        @refresh="refresh"
      />

      <v-alert v-if="dataError" type="error" class="my-4">
        {{ dataError }}
      </v-alert>

      <v-row class="my-4">
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Conversas em aberto"
            :subtitle="'Periodo: ' + rangeLabel"
            :value="String(openChats)"
            icon="mdi-message-text"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Conversas fechadas"
            :subtitle="'Periodo: ' + rangeLabel"
            :value="String(closedChats)"
            icon="mdi-message-text-outline"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Nota de satisfacao"
            :subtitle="feedbackSubtitle"
            :value="feedbackValue"
            icon="mdi-star"
            accent="success"
          />
        </v-col>
      </v-row>

      <v-row class="my-4">
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Tempo medio de resposta"
            :subtitle="'Periodo: ' + rangeLabel"
            :value="avgResponseTimeLabel"
            icon="mdi-clock-fast"
            accent="warning"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Tempo medio de atendimento"
            :subtitle="'Periodo: ' + rangeLabel"
            :value="avgHandlingTimeLabel"
            icon="mdi-clock-outline"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <MetricCard
            title="Taxa de resposta"
            :subtitle="'Periodo: ' + rangeLabel"
            :value="metrics.responseRate > 0 ? metrics.responseRate.toFixed(1) + '%' : '--'"
            icon="mdi-percent"
          />
        </v-col>
      </v-row>

      <v-progress-linear v-if="chatsLoading" indeterminate color="primary" class="my-4" />

      <ChatTable :chats="chats" :loading="chatsLoading" class="my-4" />

      <v-row class="mt-4">
        <v-col cols="12" sm="6">
          <div class="footer-info">Ultima atualizacao: {{ lastUpdatedLabel }}</div>
        </v-col>
        <v-col cols="12" sm="6" class="text-sm-right">
          <div class="footer-info">Auto-refresh: {{ autoRefresh ? 'ativo (30s)' : 'pausado' }}</div>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import MetricCard from '@/components/huggy/MetricCard.vue'
import FilterBar from '@/components/huggy/FilterBar.vue'
import ChatTable from '@/components/huggy/ChatTable.vue'

import { formatDateTime, getRangeLabel, formatSeconds, formatDuration } from '@/utils/dateHelpers'
import { calculateMetrics } from '@/utils/huggyMetrics'

export default {
  name: 'HuggyAnalytics',
  components: {
    MetricCard,
    FilterBar,
    ChatTable
  },
  data: function () {
    return {
      autoRefreshInterval: null
    }
  },
  computed: {
    ...mapState('huggy', [
      'chats',
      'contacts',
      'feedbacks',
      'chatsLoading',
      'feedbacksLoading',
      'auxLoading',
      'dataError',
      'autoRefresh',
      'lastUpdated',
      'filters'
    ]),
    ...mapGetters('huggy', ['availableChannels', 'feedbackStats']),

    totalChats: function () {
      return this.chats.length
    },

    openChats: function () {
      var count = 0
      for (var i = 0; i < this.chats.length; i++) {
        var chat = this.chats[i]
        var status = chat.status || (chat.closedAt || chat.closed_at ? 'closed' : 'open')
        if (status === 'open') count++
      }
      return count
    },

    closedChats: function () {
      return this.totalChats - this.openChats
    },

    totalContacts: function () {
      return this.contacts.length
    },

    rangeLabel: function () {
      return getRangeLabel(this.filters)
    },

    lastUpdatedLabel: function () {
      return this.lastUpdated ? formatDateTime(this.lastUpdated) : '--'
    },

    feedbackSubtitle: function () {
      if (this.feedbackStats.total > 0) {
        return this.feedbackStats.total + ' avaliacoes' + (this.feedbacksLoading ? ' (atualizando...)' : '')
      }
      return this.feedbacksLoading ? 'Carregando notas...' : '0 avaliacoes'
    },

    feedbackValue: function () {
      if (this.feedbackStats.avgScore != null) {
        return this.feedbackStats.avgScore.toFixed(1)
      }
      return this.feedbacksLoading ? '...' : '--'
    },

    metrics: function () {
      return calculateMetrics({ chats: this.chats, contacts: this.contacts, filters: this.filters })
    },

    avgResponseTime: function () {
      var series = this.metrics.responseSeries || []
      var values = series.filter(function (v) { return v > 0 })
      if (values.length === 0) return null
      return values.reduce(function (sum, v) { return sum + v }, 0) / values.length
    },

    avgResponseTimeLabel: function () {
      return formatSeconds(this.avgResponseTime)
    },

    avgHandlingTimeLabel: function () {
      return formatDuration(this.metrics.avgHandlingMinutes)
    }
  },

  mounted: function () {
    this.fetchChats()
    this.fetchAuxiliaryData()
    this.setupAutoRefresh()
  },

  beforeDestroy: function () {
    this.clearAutoRefresh()
  },

  watch: {
    chats: function () {
      this.fetchFeedbacks()
    },
    autoRefresh: function (val) {
      if (val) {
        this.setupAutoRefresh()
      } else {
        this.clearAutoRefresh()
      }
    }
  },

  methods: {
    ...mapActions('huggy', [
      'fetchChats',
      'fetchAuxiliaryData',
      'fetchFeedbacks',
      'refresh',
      'updateFilters'
    ]),
    ...mapMutations('huggy', ['SET_AUTO_REFRESH']),

    setAutoRefresh: function (value) {
      this.SET_AUTO_REFRESH(value)
    },

    setupAutoRefresh: function () {
      this.clearAutoRefresh()
      if (this.autoRefresh) {
        var self = this
        this.autoRefreshInterval = setInterval(function () {
          self.refresh()
        }, 30000)
      }
    },

    clearAutoRefresh: function () {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval)
        this.autoRefreshInterval = null
      }
    }
  }
}
</script>

<style scoped>
.footer-info {
  font-size: 12px;
  color: #410E47;
}
</style>
